import type { Rng } from "../rng";
import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	reject,
} from "../types";

/** A generic four-colour deck — no borrowed branding, just colours and numbers. */
export const CARD_COLOURS = ["🔴", "🔵", "🟡", "🟢"] as const;
export const LAST_CARD_HAND = 7;

/** `value` 0–9 are numbers; 10 skips a turn; 11 makes them draw two. */
export interface LastCard {
	colour: number;
	value: number;
}

export const SKIP = 10;
export const DRAW_TWO = 11;

export interface LastCardState extends BaseState {
	kind: "lastcard";
	hands: [LastCard[], LastCard[]];
	counts: [number, number];
	/** Face down, so it is redacted away. */
	pile: LastCard[];
	pileLeft: number;
	top: LastCard;
	lastNote: string | null;
}

export type LastCardMove = { play: number } | { draw: true };

function makeDeck(rng: Rng): LastCard[] {
	const deck: LastCard[] = [];
	for (let colour = 0; colour < CARD_COLOURS.length; colour++) {
		for (let value = 0; value <= 9; value++) {
			deck.push({ colour, value });
			if (value > 0) deck.push({ colour, value });
		}
		deck.push({ colour, value: SKIP });
		deck.push({ colour, value: DRAW_TWO });
	}
	return rng.shuffle(deck);
}

export function cardLabel(card: LastCard): string {
	if (card.value === SKIP) return "⛔";
	if (card.value === DRAW_TWO) return "+2";
	return String(card.value);
}

function playable(card: LastCard, top: LastCard): boolean {
	return card.colour === top.colour || card.value === top.value;
}

export const lastCard: GameDefinition<LastCardState, LastCardMove> = {
	id: "lastcard",
	category: "cards",
	title: "Last Card Duel",
	emoji: "🎴",
	blurb: "Match the colour or the number. First to empty their hand wins.",
	howTo: [
		"Play a card that matches the top card's colour or number.",
		"⛔ makes your friend miss a go. +2 makes them pick up two.",
		"Nothing to play? Take a card from the pile.",
		"First to get rid of all their cards wins.",
	],

	create(rng) {
		const deck = makeDeck(rng);
		const first = deck.splice(0, LAST_CARD_HAND);
		const second = deck.splice(0, LAST_CARD_HAND);
		// Start on a plain number, so nobody is punished before their first go.
		const startIndex = deck.findIndex((card) => card.value <= 9);
		const top = deck.splice(startIndex === -1 ? 0 : startIndex, 1)[0];

		return {
			kind: "lastcard",
			hands: [first, second],
			counts: [first.length, second.length],
			pile: deck,
			pileLeft: deck.length,
			top,
			lastNote: null,
			turn: 0,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const asDraw = (raw as { draw?: unknown }).draw;
		if (asDraw === true) return { draw: true };
		const play = (raw as { play?: unknown }).play;
		if (typeof play !== "number" || !Number.isInteger(play) || play < 0) return null;
		return { play };
	},

	apply(state, player, move): MoveOutcome<LastCardState> {
		if (state.result) return reject("This game is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");

		const them = otherPlayer(player);
		const hands: [LastCard[], LastCard[]] = [[...state.hands[0]], [...state.hands[1]]];
		const pile = [...state.pile];

		if ("draw" in move) {
			if (hands[player].some((card) => playable(card, state.top))) {
				return reject("You have a card you can play!");
			}
			const drawn = pile.pop();
			if (drawn) hands[player].push(drawn);

			const next: LastCardState = {
				...state,
				hands,
				counts: [hands[0].length, hands[1].length],
				pile,
				pileLeft: pile.length,
				lastNote: drawn ? "Picked up a card." : "The pile is empty!",
				turn: them,
			};

			// Cards played are gone rather than recycled, so the deck only ever
			// shrinks. Once the pile is out and neither hand has a card that
			// matches, drawing does nothing and the turn just flips — for ever.
			// Worse than a stuck game: the turn clock keeps playing that empty
			// draw, which looks like activity, so the room never goes idle and
			// never shuts down. Whoever is holding fewer cards takes it.
			const stuck =
				pile.length === 0 &&
				!hands[0].some((card) => playable(card, state.top)) &&
				!hands[1].some((card) => playable(card, state.top));
			if (stuck) {
				const winner =
					hands[0].length === hands[1].length ? null : hands[0].length < hands[1].length ? 0 : 1;
				return {
					ok: true,
					state: { ...next, turn: null, result: { winner }, lastNote: "Nobody can go!" },
				};
			}

			return { ok: true, state: next };
		}

		const card = hands[player][move.play];
		if (!card) return reject("You don't have that card.");
		if (!playable(card, state.top)) return reject("That doesn't match the colour or the number.");

		hands[player].splice(move.play, 1);
		let note = `Played ${CARD_COLOURS[card.colour]} ${cardLabel(card)}.`;
		let skipThem = false;

		if (card.value === SKIP) {
			skipThem = true;
			note = "⛔ They miss a go!";
		} else if (card.value === DRAW_TWO) {
			for (let i = 0; i < 2; i++) {
				const drawn = pile.pop();
				if (drawn) hands[them].push(drawn);
			}
			skipThem = true;
			note = "+2 — they pick up two and miss a go!";
		}

		const won = hands[player].length === 0;
		return {
			ok: true,
			state: {
				...state,
				hands,
				counts: [hands[0].length, hands[1].length],
				pile,
				pileLeft: pile.length,
				top: card,
				lastNote: note,
				scores: won ? (player === 0 ? [1, 0] : [0, 1]) : state.scores,
				turn: won ? null : skipThem ? player : them,
				result: won ? { winner: player } : null,
			},
		};
	},

	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		const moves: LastCardMove[] = [];
		state.hands[player].forEach((card, index) => {
			if (playable(card, state.top)) moves.push({ play: index });
		});
		// Drawing is only allowed when there is genuinely nothing to play.
		return moves.length > 0 ? moves : [{ draw: true }];
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},

	redact(state, viewer) {
		const them = otherPlayer(viewer);
		const hands: [LastCard[], LastCard[]] = [[], []];
		hands[viewer] = state.hands[viewer];
		hands[them] = [];
		return { ...state, hands, pile: [] };
	},
};
